/**
 * Karbon360° v2 — navigation model.
 *
 * Single source of truth for the sidebar tree, the breadcrumb trail and the ⌘K
 * command list, so the three can never disagree.
 *
 * LANGUAGE follows the design spec (v2/CLAUDE.md): Albanian on data-entry
 * surfaces, English on analysis surfaces and structural labels.
 *
 * DELIBERATELY NOT LISTED — routes that exist in the app but are kept out of the
 * nav. Nothing is deleted; these are simply not advertised until they have a
 * working backend (user instruction, 2026-07-28: "dont remove anything, just
 * dont show them on the app"):
 *   /scope-1/emetimet-fugjitive  — page exists, but no F-gas factors or calc
 *   Scope 2 purchased steam      — no table, no factor, no endpoint
 *   Scope 3 waste / water / purchased goods — label-only stubs, no factors
 *   /recipes, /invoices-new alias /invoices, /badge — unchanged from before
 *
 * NOT YET PRESENT: Targets & pathway. A real part of the
 * approved IA with no screen yet; they appear as their phases land rather than
 * shipping nav entries that lead nowhere. (The Emission factors library is
 * deliberately out of scope — see the Measure group below.)
 *
 * The Action inbox has BOTH surfaces, per user decision 2026-07-30: a summary
 * card on Overview showing the top items, and its own screen with the full list.
 */

export const NAV = [
  {
    groupKey: null,
    items: [
      { id: 'overview', labelKey: 'nav.overview', icon: 'Home', to: '/overview' },
      // Badge count is injected by SidebarV2 from useActionInbox — NAV is static.
      { id: 'inbox', labelKey: 'nav.inbox', icon: 'Inbox', to: '/action-inbox' },
    ],
  },
  {
    groupKey: 'nav.group.collect',
    items: [
      { id: 'monthly', labelKey: 'nav.monthly', icon: 'Cal', to: '/regjistrimi' },
      {
        id: 'collect', labelKey: 'nav.collect', icon: 'Upload',
        kids: [
          {
            id: 'collect.s1', labelKey: 'nav.s1',
            kids: [
              { id: 'collect.s1.stationary', labelKey: 'nav.stationary',    to: '/scope-1/djegja-stacionare' },
              { id: 'collect.s1.mobile',     labelKey: 'nav.mobile',        to: '/scope-1/djegja-mobile' },
              { id: 'collect.s1.process',    labelKey: 'nav.process', to: '/scope-1/emetimet-nga-proceset' },
            ],
          },
          {
            id: 'collect.s2', labelKey: 'nav.s2',
            kids: [
              { id: 'collect.s2.electricity', labelKey: 'nav.electricity',   to: '/scope-2/electricity' },
              { id: 'collect.s2.heat',        labelKey: 'nav.heat',  to: '/scope-2/emetimet-nga-proceset' },
            ],
          },
          {
            id: 'collect.s3', labelKey: 'nav.s3',
            kids: [
              // Phase 3 splits business travel and commuting into their own
              // screens; today both live on the single /scope-3 page.
              { id: 'collect.s3.travel', labelKey: 'nav.travel', to: '/scope-3' },
            ],
          },
        ],
      },
      { id: 'records',    labelKey: 'nav.records',    icon: 'Doc',      to: '/records' },
      { id: 'facilities', labelKey: 'nav.facilities', icon: 'Building', to: '/facilities' },
    ],
  },
  {
    groupKey: 'nav.group.measure',
    items: [
      // Emissions explorer joins this group next. The Emission factors library is
      // deliberately NOT built — factors are IP; the per-record calculation trace
      // still shows the factor behind each figure, so a customer can defend their
      // own numbers without anyone getting a scrapeable catalogue.
      { id: 'explorer', labelKey: 'nav.explorer', icon: 'Chart', to: '/explorer' },
      { id: 'quality', labelKey: 'nav.quality', icon: 'Shield', to: '/data-quality' },
    ],
  },
  {
    groupKey: 'nav.group.report',
    items: [
      // /emissions is where the GHG report is generated (Export PDF, logo, the
      // report-parity cards), so it must stay reachable from the nav.
      { id: 'reports', labelKey: 'nav.reports', icon: 'Chart', to: '/emissions' },
      { id: 'reports.detail', labelKey: 'nav.reportsDetail', icon: 'Doc', to: '/emissions/detailed' },
    ],
  },
  {
    // CBAM is plan-gated (billing_plan compliance/enterprise, or admin/owner).
    // NAV stays static like everywhere else — SidebarV2 filters this group out
    // until the session resolves with the entitlement, and buildCommands below
    // takes a `cbam` flag so ⌘K cannot advertise what the sidebar hides.
    groupKey: 'nav.group.cbam',
    items: [
      { id: 'cbam', labelKey: 'nav.cbam', icon: 'Globe', to: '/cbam' },
      { id: 'cbam.installations', labelKey: 'nav.cbamInstallations', icon: 'Factory', to: '/cbam/installations' },
      { id: 'cbam.goods', labelKey: 'nav.cbamGoods', icon: 'Layers', to: '/cbam/goods' },
    ],
  },
];

/** The one plan-gated group, named once so the sidebar and palette agree. */
export const CBAM_GROUP_KEY = 'nav.group.cbam';

/**
 * Owner/admin destinations. Deliberately NOT part of NAV — they are reached from
 * the account menu, so the sidebar is identical for every account.
 *
 * The old client-side ADMIN_EMAILS list is gone. It could not be changed without
 * a redeploy and it disagreed with the server's copy, so the UI offered admin nav
 * to an account the API rejected. Visibility now derives from users.role via
 * useSession(); the API enforces it independently.
 */
export const ADMIN_NAV = [
  { id: 'admin.users',  labelKey: 'nav.admin.users',       icon: 'Users',  to: '/admin/users' },
  { id: 'admin.tokens', labelKey: 'nav.admin.tokens', icon: 'Shield', to: '/admin/tokens' },
];

/** Breadcrumb trail per route, as translation keys. Longest matching path wins. */
export const CRUMBS = {
  '/overview':                      ['nav.overview'],
  '/action-inbox':                  ['nav.inbox'],
  '/emissions':                     ['nav.group.report', 'nav.reports'],
  '/emissions/detailed':            ['nav.group.report', 'nav.reportsDetail'],
  '/regjistrimi':                   ['nav.group.collect', 'nav.monthly'],
  '/scope-1/djegja-stacionare':     ['nav.group.collect', 'nav.collect', 'nav.s1', 'nav.stationary'],
  '/scope-1/djegja-mobile':         ['nav.group.collect', 'nav.collect', 'nav.s1', 'nav.mobile'],
  '/scope-1/emetimet-nga-proceset': ['nav.group.collect', 'nav.collect', 'nav.s1', 'nav.process'],
  '/scope-2/electricity':           ['nav.group.collect', 'nav.collect', 'nav.s2', 'nav.electricity'],
  '/scope-2/emetimet-nga-proceset': ['nav.group.collect', 'nav.collect', 'nav.s2', 'nav.heat'],
  '/scope-3':                       ['nav.group.collect', 'nav.collect', 'nav.s3', 'nav.travel'],
  '/records':                       ['nav.group.collect', 'nav.records'],
  '/facilities':                    ['nav.group.collect', 'nav.facilities'],
  '/explorer':                      ['nav.group.measure', 'nav.explorer'],
  '/data-quality':                  ['nav.group.measure', 'nav.quality'],
  '/cbam':                          ['nav.group.cbam', 'nav.cbam'],
  '/cbam/installations':            ['nav.group.cbam', 'nav.cbamInstallations'],
  '/cbam/goods':                    ['nav.group.cbam', 'nav.cbamGoods'],
  '/admin/users':                   ['chrome.admin', 'nav.admin.users'],
  '/admin/tokens':                  ['chrome.admin', 'nav.admin.tokens'],
  '/account':                       ['chrome.account'],
};

export function crumbsFor(pathname) {
  if (CRUMBS[pathname]) return CRUMBS[pathname];
  const hit = Object.keys(CRUMBS)
    .filter((p) => pathname.startsWith(p))
    .sort((a, b) => b.length - a.length)[0];
  return hit ? CRUMBS[hit] : ['Karbon360°'];
}

/** ⌘K command list — derived from NAV so the two cannot drift apart. */
function walk(items, out) {
  items.forEach((it) => {
    if (it.to) out.push({ groupKey: 'chrome.goTo', labelKey: it.labelKey, to: it.to, icon: it.icon || 'Doc' });
    if (it.kids) walk(it.kids, out);
  });
  return out;
}

export function buildCommands({ isAdmin = false, cbam = false } = {}) {
  const go = [];
  // The CBAM group only enters the palette once the caller knows the plan
  // qualifies — same hide-until-known rule the sidebar applies.
  NAV.forEach((g) => {
    if (g.groupKey === CBAM_GROUP_KEY && !cbam) return;
    walk(g.items, go);
  });

  // Give the collect leaves their own group and category icons.
  const ICONS = {
    '/scope-1/djegja-stacionare':     'Flame',
    '/scope-1/djegja-mobile':         'Truck',
    '/scope-1/emetimet-nga-proceset': 'Factory',
    '/scope-2/electricity':           'Bolt',
    '/scope-2/emetimet-nga-proceset': 'Flame',
    '/scope-3':                       'Users',
  };
  const tagged = go.map((c) =>
    ICONS[c.to] ? { ...c, groupKey: 'chrome.collectData', icon: ICONS[c.to] } : c
  );

  if (isAdmin) {
    ADMIN_NAV.forEach((a) => tagged.push({ groupKey: 'chrome.admin', labelKey: a.labelKey, to: a.to, icon: a.icon }));
  }

  // Collect-data entries sit in the middle of the nav tree, so walking it in
  // order interleaves them with "Go to" items and the palette would render the
  // same group heading twice. Partition into contiguous blocks, preserving the
  // original order within each.
  const ORDER = ['chrome.goTo', 'chrome.collectData', 'chrome.admin'];
  return ORDER.flatMap((g) => tagged.filter((c) => c.groupKey === g));
}

/** Reporting-period options shown in the topbar switcher. */
export const PERIOD_OPTIONS = [
  'Last 12 months',
  'Calendar 2026',
  'Calendar 2025',
  'Q1 2026',
  'Q2 2026',
];
